import React, { useEffect } from 'react';
import { MoreHorizontal, Package, DollarSign, TrendingUp, ShoppingCart, AlertCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboard } from '../redux/DashboardSlice';

const Overview = () => {
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector((state) => state.dashboard);

    useEffect(() => {
        dispatch(fetchDashboard());
    }, [dispatch]);

    const formatCurrency = (amount) => {
        return `£${parseFloat(amount || 0).toFixed(2)}`;
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}m`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
        return num?.toString() || '0';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculateStoreVisitPercentages = () => {
        if (!data?.storevisits) return { day: 0, week: 0, month: 0 };
        const { day, week, month } = data?.storevisits;
        const total = month || 1;
        
        return {
            day: Math.round((day / total) * 100),
            week: Math.round((week / total) * 100),
            month: 100
        };
    };

    const generateChartPath = () => {
        if (!data?.graphData || data?.graphData.length === 0) return '';
        
        const width = 400;
        const height = 200;
        const padding = 20;
        const maxAmount = Math.max(...data?.graphData?.map(d => d.amount), 1);
        const points = data?.graphData.length;
        const stepX = (width - padding * 2) / (points - 1);

        let path = '';
        data?.graphData.forEach((item, index) => {
            const x = padding + index * stepX;
            const y = height - padding - ((item.amount / maxAmount) * (height - padding * 2));
            
            if (index === 0) {
                path += `M${x},${y}`;
            } else {
                const prevX = padding + (index - 1) * stepX;
                const prevY = height - padding - ((data?.graphData[index - 1].amount / maxAmount) * (height - padding * 2));
                const cpX = (prevX + x) / 2;
                path += ` Q${cpX},${prevY} ${x},${y}`;
            }
        });

        return path;
    };

    const generateAreaPath = () => {
        const linePath = generateChartPath();
        if (!linePath) return '';
        return `${linePath} L400,200 L20,200 Z`;
    };

    if (loading) {
        return (
            <div className="p-6 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center">
                    <AlertCircle className="text-red-600 mr-3" size={24} />
                    <div>
                        <h3 className="text-red-900 font-semibold">Error loading dashboard</h3>
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const stats = [
        {
            title: 'Orders Completed',
            value: formatNumber(data?.orderCompleted || 0),
            color: 'orange',
            icon: Package,
            bgColor: 'bg-orange-100',
            iconColor: 'text-orange-600'
        },
        {
            title: 'Total revenue made',
            value: formatCurrency(data?.totalRevenueMade || 0),
            color: 'purple',
            icon: DollarSign,
            bgColor: 'bg-purple-100',
            iconColor: 'text-purple-600'
        },
        {
            title: 'Total store visits',
            value: formatNumber(data?.totalStoreVisit || 0),
            color: 'blue',
            icon: TrendingUp,
            bgColor: 'bg-blue-100',
            iconColor: 'text-blue-600'
        },
        {
            title: 'Total sales made',
            value: formatCurrency(data?.totalSalesMade || 0),
            color: 'pink',
            icon: ShoppingCart,
            bgColor: 'bg-pink-100',
            iconColor: 'text-pink-600'
        }
    ];

    const visitPercentages = calculateStoreVisitPercentages();

    return (
        <div className="p-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div key={index} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm mb-1">{stat.title}</p>
                                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                                </div>
                                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                                    <Icon className={stat.iconColor} size={24} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Chart */}
                <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Revenue this week</h3>
                            <p className="text-3xl font-bold text-gray-900 mt-2">
                                {formatCurrency(data?.revenueThisWeek || 0)}
                            </p>
                        </div>
                        <div className="bg-purple-600 text-white px-3 py-1 rounded-lg text-sm">
                            {formatCurrency(data?.revenueThisWeek || 0)}
                        </div>
                    </div>

                    {/* Chart */}
                    <div className="relative h-64">
                        <svg className="w-full h-full" viewBox="0 0 400 200">
                            <defs>
                                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 0.3 }} />
                                    <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 0 }} />
                                </linearGradient>
                            </defs>
                            {generateChartPath() && (
                                <>
                                    <path
                                        d={generateChartPath()}
                                        stroke="#8b5cf6"
                                        strokeWidth="3"
                                        fill="none"
                                    />
                                    <path
                                        d={generateAreaPath()}
                                        fill="url(#gradient)"
                                    />
                                    {data?.graphData?.map((item, index) => {
                                        const width = 400;
                                        const height = 200;
                                        const padding = 20;
                                        const maxAmount = Math.max(...data?.graphData.map(d => d.amount), 1);
                                        const stepX = (width - padding * 2) / (data?.graphData.length - 1);
                                        const x = padding + index * stepX;
                                        const y = height - padding - ((item.amount / maxAmount) * (height - padding * 2));
                                        
                                        return item.amount > 0 ? (
                                            <circle key={index} cx={x} cy={y} r="4" fill="#8b5cf6" />
                                        ) : null;
                                    })}
                                </>
                            )}
                        </svg>
                        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 px-4">
                            {data?.graphData?.map((item, index) => (
                                <span key={index}>{item.month.substring(0, 3)}</span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Store Visits */}
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Store visits</h3>
                        <MoreHorizontal size={20} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Details about your store visits</p>

                    <div className="space-y-4">
                        <div className="text-center mb-6">
                            <div className="text-4xl font-bold text-gray-900">{data?.totalStoreVisit || 0}</div>
                            <div className="text-sm text-gray-500">Total Visits</div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Daily ({data?.storevisits?.day || 0})</span>
                                <span className="text-sm font-medium">{visitPercentages?.day}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className="bg-orange-500 h-2 rounded-full transition-all" 
                                    style={{ width: `${visitPercentages?.day}%` }}
                                ></div>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Weekly ({data?.storevisits?.week || 0})</span>
                                <span className="text-sm font-medium">{visitPercentages?.week}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className="bg-purple-600 h-2 rounded-full transition-all" 
                                    style={{ width: `${visitPercentages?.week}%` }}
                                ></div>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Monthly ({data?.storevisits?.month || 0})</span>
                                <span className="text-sm font-medium">{visitPercentages?.month}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all" 
                                    style={{ width: `${visitPercentages?.month}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Top Products */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Top Products</h3>
                        <p className="text-sm text-gray-500">Best selling products in your store</p>
                    </div>
                    <MoreHorizontal size={20} className="text-gray-400" />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="text-left text-sm font-medium text-gray-500 pb-3">Product name</th>
                                <th className="text-left text-sm font-medium text-gray-500 pb-3">Token</th>
                                <th className="text-left text-sm font-medium text-gray-500 pb-3">Date added</th>
                                <th className="text-left text-sm font-medium text-gray-500 pb-3">Price</th>
                                <th className="text-left text-sm font-medium text-gray-500 pb-3">Total Earning</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.topProducts && data?.topProducts.length > 0 ? (
                                data?.topProducts.map((product, index) => (
                                    <tr key={index} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                                        <td className="py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                    <Package size={20} className="text-purple-600" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {product.productName}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                                {product.productToken}
                                            </span>
                                        </td>
                                        <td className="py-4 text-sm text-gray-500">
                                            {formatDate(product.dateAdded)}
                                        </td>
                                        <td className="py-4 text-sm font-medium text-orange-600">
                                            {formatCurrency(product.price)}
                                        </td>
                                        <td className="py-4 text-sm font-medium text-teal-600">
                                            {formatCurrency(product.totalEarning)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="py-12 text-center text-gray-500">
                                        <Package className="mx-auto mb-3 text-gray-400" size={48} />
                                        <p>No products data available</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Overview;